import update from 'immutability-helper';
import _ from 'lodash';
import moment from 'moment';
import { createAction } from '~reduxHelpers';
import { addToast } from '~features/toast/ducks';
import { findFormByCustomContent } from '~utils/common.utils';
import { handleRedirection } from '../../forms/ducks';

// Services
import * as api from '~services/api'; // eslint-disable-line

import { ITEMS_PER_PAGE } from './helpers';

export const initialState = {
  loading: false,
  error: null,
  items: {},
  cartItems: [],
  detailsItem: null,
  hierarchyOptions: [],
  hierarchy: [],
  previousOrders: { content: [], stale: true },
  searchTerm: '',
  sortMode: {
    value: 'price,asc',
    label: 'Edullisin ensin',
  },
  failedFetch: false,
  deliveryUnit: null,
  deliveryAddress: null,
  payer: null,
  doneOrder: null,
  submitError: false,
};

function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case 'RECEIVE_ITEMS': {
      const { items, append } = action.payload;

      if (append) {
        return {
          ...state,
          items: {
            ...items,
            content: [...state.items.content, ...items.content],
          },
        };
      }
      // replace
      return { ...state, items };
    }
    case 'SET_LOADING':
      return update(state, { loading: { $set: action.payload } });
    case 'UPDATE_DETAILS_ITEM':
      return update(state, { detailsItem: { $set: action.payload } });
    case 'ADD_ITEM_TO_CART':
      return { ...state, cartItems: [...state.cartItems, action.payload] };
    case 'REMOVE_ITEM_FROM_CART': {
      const newCartItems = state.cartItems.filter(
        item => item.productCode !== action.payload
      );
      return { ...state, cartItems: newCartItems };
    }
    case 'EMPTY_CART_FROM_ITEMS':
      return { ...state, cartItems: [] };
    case 'UPDATE_CART_ITEM_COUNT': {
      const { key, count } = action.payload;
      const item = state.cartItems.find(item => item.productCode === key);
      if (!item) {
        console.debug("Item wasn't found in UPDATE_CART_ITEM_COUNT");
      }
      const newItem = { ...item, itemCount: count };
      const withoutOld = state.cartItems.filter(
        item => item.productCode !== key
      );
      return { ...state, cartItems: [...withoutOld, newItem] };
    }
    case 'RECEIVE_HIERARCHY_OPTIONS':
      return update(state, { hierarchyOptions: { $set: action.payload } });
    case 'RECEIVE_HIERARCHY_ADD': {
      return { ...state, hierarchy: [...state.hierarchy, action.payload] };
    }
    case 'RECEIVE_HIERARCHY_REMOVE': {
      const idx = state.hierarchy.findIndex(
        item => item.key === action.payload.key
      );
      if (idx === -1) return state;
      return { ...state, hierarchy: [..._.slice(state.hierarchy, 0, idx)] };
    }
    case 'SET_HIERARCHY_LOADING': {
      return update(state, { hierarchyLoading: { $set: action.payload } });
    }
    case 'RECEIVE_PREVIOUS_ORDERS':
      return update(state, { previousOrders: { $set: action.payload } });
    case 'SET_SEARCH_TERM':
      return update(state, { searchTerm: { $set: action.payload } });
    case 'SET_SORT_MODE':
      return update(state, { sortMode: { $set: action.payload } });
    case 'SET_FAILED_FETCH':
      return update(state, { failedFetch: { $set: action.payload } });
    case 'SET_DELIVERY_UNIT':
      return update(state, { deliveryUnit: { $set: action.payload } });
    case 'SET_DELIVERY_ADDRESS':
      return update(state, { deliveryAddress: { $set: action.payload } });
    case 'SET_PAYER':
      return update(state, { payer: { $set: action.payload } });
    case 'SET_DONE_ORDER':
      return update(state, { doneOrder: { $set: action.payload } });
    case 'SET_SUBMIT_ERROR':
      return update(state, { submitError: { $set: action.payload } });
    default:
      return state;
  }
}

// Actions
export const setLoading = createAction('SET_LOADING');
export const setFailedFetch = createAction('SET_FAILED_FETCH');
export const receiveItems = createAction('RECEIVE_ITEMS');
export const updateDetailsItem = createAction('UPDATE_DETAILS_ITEM');
export const addItemToCart = createAction('ADD_ITEM_TO_CART');
export const removeItemFromCart = createAction('REMOVE_ITEM_FROM_CART');
export const emptyCartFromItems = createAction('EMPTY_CART_FROM_ITEMS');
export const updateCartItemCount = createAction('UPDATE_CART_ITEM_COUNT');
export const receiveHierarchyOptions = createAction(
  'RECEIVE_HIERARCHY_OPTIONS'
);
export const receiveHierarchyAdd = createAction('RECEIVE_HIERARCHY_ADD');
export const receiveHierarchyRemove = createAction('RECEIVE_HIERARCHY_REMOVE');
export const setHierarchyLoading = createAction('SET_HIERARCHY_LOADING');
export const receivePreviousOrders = createAction('RECEIVE_PREVIOUS_ORDERS');
export const setSearchTerm = createAction('SET_SEARCH_TERM');
export const setSortModeAction = createAction('SET_SORT_MODE');
export const setDeliveryUnit = createAction('SET_DELIVERY_UNIT');
export const setDeliveryAddress = createAction('SET_DELIVERY_ADDRESS');
export const setPayer = createAction('SET_PAYER');
export const setDoneOrder = createAction('SET_DONE_ORDER');
export const setSubmitError = createAction('SET_SUBMIT_ERROR');

// Selectors
export const getItems = state => state.webshop.items;
export const getCartItems = state => state.webshop.cartItems;
export const getDetailsItem = state => state.webshop.detailsItem;
export const getHierarchy = state => state.webshop.hierarchy;
export const getHierarchyOptions = state => state.webshop.hierarchyOptions;
export const getHierarchyLoading = state => state.webshop.hierarchyLoading;
export const getPreviousOrders = state => state.webshop.previousOrders;
export const getSortMode = state => state.webshop.sortMode;
export const getLoading = state => state.webshop.loading;
export const getFailedFetch = state => state.webshop.failedFetch;
export const getSearchTerm = state => state.webshop.searchTerm;
export const getDoneOrder = state => state.webshop.doneOrder;
export const getSubmitError = state => state.webshop.submitError;

// Thunks
export const fetchItems = params => async (dispatch, getState) => {
  const state = getState().webshop;
  const navItems = getState().reducer.navlist;
  const { formId } = findFormByCustomContent('tuotteet', navItems);

  dispatch(setLoading(true));
  const { searchTerm, productCategory, number, size, append } = params || {};
  const hierarchyCategory = _.last(state.hierarchy);

  try {
    const q = searchTerm || searchTerm === '' ? searchTerm : state.searchTerm;
    const salesgroupid =
      productCategory || hierarchyCategory ? hierarchyCategory.key : '';

    if (!q && !salesgroupid) {
      console.debug('Aborting fetch: query AND salesgroupid missing');
      dispatch(receiveItems({ items: {}, append: false }));
      dispatch(setLoading(false));
      return;
    }

    let request = `inventory/form/${formId}/data/inventory/paged-search/?q=${encodeURIComponent(
      q
    )}`; // encodeURIComponent is required for IE11

    if (salesgroupid) request += `&salesgroupid=${salesgroupid}`;

    request += `&number=${number || 0}`;
    // If requesting more pages, include providerIndex in query
    if (number) {
      const providers = state.items.providerIndex;
      if (providers) {
        Object.keys(providers).forEach(key => {
          request += `&providerIndex.${key}=${providers[key]}`;
        });
      }
    }
    request += `&size=${size || ITEMS_PER_PAGE}`;
    request += `&sort=${state.sortMode.value}`;

    const items = await api.get(request);

    dispatch(receiveItems({ items, append }));
    dispatch(setLoading(false));
    dispatch(setFailedFetch(false));
  } catch (e) {
    console.error('Failed fetching webshop items', e);
    dispatch(setLoading(false));
    dispatch(setFailedFetch(true));
  }
};

const itemAlreadyInCartMessage = 'Tuote on jo ostoskorissa';
const lessThanOneItemMessage =
  'Voit lisätä ostoskoriin vähintään yhden kappaleen tuotetta';

export const addToCart = (item, count) => async (dispatch, getState) => {
  const state = getState().webshop;
  // Check if item already exists in cart
  if (
    // Items either have a productCode or an inventoryCode, hopefully
    // Try to check for both
    state.cartItems.find(
      cartItem =>
        (item.inventoryCode && cartItem.inventoryCode === item.inventoryCode) ||
        (item.productCode && cartItem.productCode === item.productCode)
    )
  ) {
    dispatch(
      addToast({
        message: itemAlreadyInCartMessage,
        type: 'INFO',
      })
    );
    return { item, error: itemAlreadyInCartMessage };
  }

  if (count < 1) {
    dispatch(
      addToast({
        message: 'Voit lisätä ostoskoriin vähintään yhden kappaleen tuotetta',
        type: 'WARNING',
      })
    );
    return { item, error: lessThanOneItemMessage };
  }

  dispatch(addItemToCart({ ...item, itemCount: count, addedAt: new Date() }));
  dispatch(
    addToast({
      message: 'Tuote lisätty ostoskoriin',
      type: 'SUCCESS',
    })
  );

  return { item, error: false };
};

export const removeFromCart = key => async dispatch => {
  dispatch(removeItemFromCart(key));
};

export const emptyCart = () => async dispatch => {
  dispatch(emptyCartFromItems());
  dispatch(
    addToast({
      message: 'Ostoskori tyhjennetty',
      type: 'SUCCESS',
    })
  );
};

export const submitCartOrder = navigate => async (dispatch, getState) => {
  const state = getState();
  // Reset error status immediately
  dispatch(setSubmitError(false));

  const { cartItems, deliveryUnit, deliveryAddress, payer } = state.webshop;

  // The server only needs these fields
  const orderRows = cartItems.map(item => ({
    id: item.productCode || item.inventoryCode,
    saleUnit: item.saleUnit,
    itemCount: item.itemCount,
    title: item.productName,
    providerId: item.providerId,
  }));

  // TODO add this once we can figure out form id by formType
  // const forms = _.flatMap(
  //   state.reducer.navlist,
  //   navObject => navObject.children
  // );
  // const formId = forms.find(
  //   form => form.formType === 'CUSTOM_FORM_PRODUCTS'
  // ).id;

  const formId = state.reducer.formModels.find(
    form => form.formType === 'CUSTOM_FORM_PRODUCTS'
  ).id;

  const orderData = {
    fields: {
      orderRows,
      targetUnit: deliveryUnit.value,
      deliveryAddress: deliveryAddress.value,
      readableDeliveryAddress: deliveryAddress.label,
      payer: payer.value,
    },
    formId,
  };
  try {
    const order = await api.post('orders/', orderData);
    dispatch(emptyCartFromItems());
    dispatch(
      setDoneOrder({
        items: cartItems,
        deliveryUnit,
        deliveryAddress,
        payer,
        user: state.user.data.name,
        date: moment().format('DD.MM.YYYY'),
      })
    );
    // For miniprojects
    dispatch(handleRedirection(order, navigate));
    dispatch(
      addToast({
        message: 'Tuotteet tilattu',
        type: 'SUCCESS',
      })
    );
  } catch (e) {
    console.error('Error in submitCartOrder', e);
    dispatch(setSubmitError(e));
    dispatch(
      addToast({
        message: 'Tuotteiden tilaus epäonnistui',
        type: 'ERROR',
      })
    );
  }
};

export const fetchHierarchyOptions = parent => async dispatch => {
  dispatch(setHierarchyLoading(true));
  const options = await api.get(
    `data/products/hierarchy${parent ? `/search?parent=${parent}` : ''}`
  );
  dispatch(receiveHierarchyOptions(options));
  dispatch(setHierarchyLoading(false));
};

// Store full hierarchy items, not just keys
export const addToHierarchy = item => async dispatch => {
  dispatch(receiveHierarchyAdd(item));
  dispatch(fetchHierarchyOptions(item.key));
  dispatch(fetchItems());
};

export const removeFromHierarchy = item => async dispatch => {
  dispatch(receiveHierarchyRemove(item));
  dispatch(fetchHierarchyOptions(item.parent));
  dispatch(fetchItems());
};

export const fetchPreviousOrders = () => async dispatch => {
  // const from = moment()
  //   .subtract(2, 'w')
  //   .toISOString();
  // const to = moment().toISOString();

  try {
    const orders = await api.get(`external-orders/`);

    dispatch(receivePreviousOrders(orders));
  } catch (e) {
    dispatch(
      addToast({
        message: 'Aiempien tilausten haku epäonnistui',
        type: 'ERROR',
      })
    );
    console.error('Error in fetchPreviousOrders', e);
  }
};

export const setSortMode = mode => async dispatch => {
  dispatch(setSortModeAction(mode));
  dispatch(fetchItems());
};

export const fetchProduct = id => async dispatch => {
  const products = await api.get(`data-by-ids/products/inventory?ids=${id}`);
  if (!products || products.length !== 1) {
    console.error('Something went wrong in fetchProduct:', products);
  }
  dispatch(updateDetailsItem(products[0]));
};

export default reducer;
